import { getMessaging, isSupported, onMessage, onRegistered, onUnregistered, register, unregister } from 'firebase/messaging'
import type { PmmRepository } from '../data/repository'
import { getPmmFirebaseServices } from '../lib/firebase/client'

let currentInstallationId = ''

export function detectPmmPlatform(): 'ios' | 'android' | 'desktop' | 'unknown' {
  if (typeof navigator === 'undefined') return 'unknown'
  const value = `${navigator.userAgent} ${navigator.platform}`.toLowerCase()
  if (/iphone|ipad|ipod/.test(value) || (value.includes('mac') && navigator.maxTouchPoints > 1)) return 'ios'
  if (value.includes('android')) return 'android'
  return value ? 'desktop' : 'unknown'
}

export function isStandalonePwa(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(display-mode: standalone)').matches
    || (navigator as Navigator & { standalone?: boolean }).standalone === true
}

export async function pushCapability(): Promise<{ supported: boolean; permission: NotificationPermission | 'unavailable'; requiresIosInstall: boolean }> {
  const supported = typeof window !== 'undefined'
    && 'Notification' in window
    && 'serviceWorker' in navigator
    && await isSupported().catch(() => false)
  return {
    supported,
    permission: supported ? Notification.permission : 'unavailable',
    requiresIosInstall: detectPmmPlatform() === 'ios' && !isStandalonePwa(),
  }
}

async function serviceWorkerRegistration(): Promise<ServiceWorkerRegistration> {
  if (!('serviceWorker' in navigator)) throw new Error('push_not_supported')
  const existing = await navigator.serviceWorker.getRegistration('/')
  if (existing) return existing
  return navigator.serviceWorker.register('/sw.js', { type: 'module', scope: '/', updateViaCache: 'none' })
}

function timeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Australia/Brisbane'
}

async function waitForRegistration(callback: () => Promise<void>): Promise<string> {
  const { app } = await getPmmFirebaseServices()
  const messaging = getMessaging(app)
  return new Promise<string>((resolve, reject) => {
    let settled = false
    const finish = (installationId: string) => {
      if (settled) return
      settled = true
      clearTimeout(timeout)
      stop()
      currentInstallationId = installationId
      resolve(installationId)
    }
    const stop = onRegistered(messaging, finish)
    const timeout = window.setTimeout(() => {
      if (settled) return
      settled = true
      stop()
      reject(new Error('push_registration_timed_out'))
    }, 15_000)
    void callback().catch((error) => {
      if (settled) return
      settled = true
      clearTimeout(timeout)
      stop()
      reject(error)
    })
  })
}

export async function enablePmmPush(repository: PmmRepository): Promise<void> {
  const capability = await pushCapability()
  if (!capability.supported) throw new Error('push_not_supported')
  if (capability.requiresIosInstall) throw new Error('ios_home_screen_install_required')
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') throw new Error(permission === 'denied' ? 'push_permission_denied' : 'push_permission_not_granted')
  const registration = await serviceWorkerRegistration()
  const { app } = await getPmmFirebaseServices()
  const messaging = getMessaging(app)
  const vapidKey = import.meta.env.VITE_PMM_FCM_VAPID_KEY?.trim()
  const installationId = await waitForRegistration(() => register(messaging, {
    serviceWorkerRegistration: registration,
    ...(vapidKey ? { vapidKey } : {}),
  }))
  await repository.registerPushInstallation({ installationId, platform: detectPmmPlatform(), timeZone: timeZone() })
}

export async function refreshPmmPushRegistration(repository: PmmRepository): Promise<void> {
  const capability = await pushCapability()
  if (!capability.supported || capability.permission !== 'granted' || capability.requiresIosInstall) return
  await enablePmmPush(repository)
}

export async function disablePmmPush(repository: PmmRepository): Promise<void> {
  const capability = await pushCapability()
  if (!capability.supported) return
  const { app } = await getPmmFirebaseServices()
  const messaging = getMessaging(app)
  const removed = new Promise<string>((resolve) => {
    const stop = onUnregistered(messaging, (installationId) => {
      stop()
      resolve(installationId)
    })
    window.setTimeout(() => { stop(); resolve(currentInstallationId) }, 8_000)
  })
  await unregister(messaging).catch(() => undefined)
  const installationId = await removed
  if (installationId) await repository.unregisterPushInstallation(installationId).catch(() => undefined)
  currentInstallationId = ''
}

export async function subscribeForegroundPmmPush(listener: () => void): Promise<() => void> {
  const capability = await pushCapability()
  if (!capability.supported) return () => undefined
  const { app } = await getPmmFirebaseServices()
  return onMessage(getMessaging(app), () => listener())
}
